import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SEO from "@/components/feature/SEO";

const coolnetCards = [
  {
    key: "why",
    icon: "ri-award-line",
    href: "/coolnet/por-que-ventracore",
  },
  {
    key: "pac",
    icon: "ri-temp-cold-line",
    href: "/coolnet/aire-acondicionado-precision",
  },
  {
    key: "liquid",
    icon: "ri-drop-line",
    href: "/coolnet/refrigeracion-liquida",
  },
  {
    key: "ups",
    icon: "ri-battery-charge-line",
    href: "/coolnet/sistema-ups",
  },
  {
    key: "integrated",
    icon: "ri-server-line",
    href: "/coolnet/solucion-integrada",
  },
];

export default function CoolnetLandingPage() {
  const { t } = useTranslation("home");

  return (
    <Layout>
      <SEO
        title="Distribuidor Autorizado Coolnet en México | Ventra Core"
        description="Ventra Core es distribuidor autorizado de Coolnet en México. Soluciones de enfriamiento de precisión, refrigeración líquida, UPS y soluciones integradas para centros de datos."
        keywords="Coolnet México, distribuidor Coolnet, aire acondicionado precisión, refrigeración líquida, UPS centros de datos, solución integrada data center"
        canonical="https://ventracore.com.mx/coolnet"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://ventracore.com.mx/coolnet#webpage",
            "url": "https://ventracore.com.mx/coolnet",
            "name": "Distribuidor Autorizado Coolnet en México | Ventra Core",
            "isPartOf": { "@id": "https://ventracore.com.mx/#website" },
            "description": "Ventra Core es distribuidor autorizado de Coolnet en México. Soluciones de enfriamiento para centros de datos.",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Inicio",
                "item": "https://ventracore.com.mx/",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Coolnet",
                "item": "https://ventracore.com.mx/coolnet",
              },
            ],
          },
        ]}
      />
      {/* Hero */}
      <section className="relative w-full bg-magenta py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20data%20center%20server%20room%20with%20rows%20of%20servers%20and%20precision%20cooling%20units%20in%20a%20clean%20organized%20environment%20with%20blue%20LED%20lighting%20and%20metallic%20infrastructure&width=1920&height=600&seq=vc-cn-hero-20&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-vip-crown-line" />
            </span>
            Authorized Distributor
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 uppercase tracking-wide">
            {t("coolnet.title")}
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-3xl mx-auto leading-relaxed">
            {t("coolnet.description")}
          </p>
          <div className="w-20 h-1 bg-white/50 mx-auto mt-6" />
        </div>
      </section>

      {/* Cards Grid */}
      <section className="w-full bg-white py-12 md:py-20">
        <div className="w-full px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-magenta text-center mb-10 md:mb-14 uppercase tracking-wide">
            {t("nav.coolnet")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {coolnetCards.map((card) => (
              <Link
                key={card.key}
                to={card.href}
                className="group bg-surface rounded-lg p-6 hover:bg-white border border-transparent hover:border-gray-100 hover:shadow-md transition-all duration-300 flex flex-col items-center text-center cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-magenta/10 text-magenta mb-4 group-hover:bg-magenta group-hover:text-white transition-colors">
                  <span className="w-7 h-7 flex items-center justify-center">
                    <i className={card.icon} />
                  </span>
                </div>
                <h3 className="text-base md:text-lg font-bold text-text mb-2">
                  {t(`nav.coolnet.${card.key}`)}
                </h3>
                <span className="w-8 h-8 flex items-center justify-center text-magenta/60 group-hover:text-magenta transition-colors mt-2">
                  <i className="ri-arrow-right-line text-xl" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="w-full bg-brandgreen py-10 md:py-14">
        <div className="w-full px-4 md:px-6 text-center">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-4 uppercase tracking-wide">
            {t("coolnetPage.cta")}
          </h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+524772847060"
              className="inline-flex items-center gap-2 bg-white text-brandgreen font-semibold px-6 py-3 rounded-md hover:bg-surface transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line" />
              </span>
              +52 477 284 70 60
            </a>
            <a
              href="mailto:comercial@ventracore.com.mx"
              className="inline-flex items-center gap-2 bg-white text-brandgreen font-semibold px-6 py-3 rounded-md hover:bg-surface transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line" />
              </span>
              comercial@ventracore.com.mx
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}